import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { trackEvent } from "@/lib/analytics";
import { saveGameState } from "./game/GamePersistenceV2";
import StreakOverlay from "./StreakOverlay";
import { determineOverlayState } from "./game/managers/OverlayManager";
import { getWordForDay } from "@/lib/wordLibrary";
import { findNewLetterIndex, getNewLetter } from "@/lib/types/WordSequence";
import { ArrowRight, Book, Plus, Target } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { useGameMode } from "@/contexts/GameModeContext";
import { useNavigate } from "react-router-dom";
import { modeAwareStorage } from '@/lib/managers/ModeAwareStorageManager';

declare global {
  interface Window {
    showInstructions?: () => void;
  }
}

// Keep the existing example sequence function
const getExampleSequence = () => {
  // Get current game mode
  const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
  
  // Get the actual game sequence for the current date
  const todaySequence = getWordForDay();
  
  // Create a consistently different seed for each user to create varied examples
  const userRandomSeed = parseInt(localStorage.getItem('babblegram_user_seed') || '0');
  const seedBase = userRandomSeed || Math.floor(Math.random() * 1000);
  
  // Store the seed for consistency if not already stored
  if (!userRandomSeed) {
    localStorage.setItem('babblegram_user_seed', seedBase.toString());
  }
  
  // Determine a random day offset based on the seed, always at least 20 days away
  // to ensure a completely different sequence from any realistic game sequence
  const randomDayOffset = 20 + (seedBase % 30);
  
  // Create a date for getting an example sequence that's far from today
  const randomDate = new Date();
  
  // Use different year for practice mode to ensure different sequences
  if (currentMode === 'practice') {
    randomDate.setFullYear(randomDate.getFullYear() - 1);
  } else {
    randomDate.setFullYear(randomDate.getFullYear() + 1);
  }
  
  // Also add the day offset
  randomDate.setDate(randomDate.getDate() + randomDayOffset);
  
  // Get an example sequence using this far-away date
  let exampleSequence = getWordForDay(randomDate);
  
  // Verify the example is different from the actual game sequence
  if (exampleSequence[0] === todaySequence[0]) {
    console.log('Example sequence matched game sequence, getting a different example');
    // Move even further away in time to get a different sequence
    randomDate.setDate(randomDate.getDate() + 45);
    exampleSequence = getWordForDay(randomDate);
    
    // Double-check - if still matching, use a hardcoded fallback sequence
    if (exampleSequence[0] === todaySequence[0]) {
      console.log('Still matched, using hardcoded fallback example');
      return ['cat', 'cats', 'caste', 'castle'];
    }
  }
  
  console.log('Using example sequence:', {
    firstWord: exampleSequence[0],
    gameFirstWord: todaySequence[0],
    match: exampleSequence[0] === todaySequence[0],
    mode: currentMode,
    exampleDate: randomDate.toISOString(),
    randomDayOffset
  });
  
  return exampleSequence;
};

const InstructionsOverlay = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showStreak, setShowStreak] = useState(false);
  const [isGameInProgress, setIsGameInProgress] = useState(false);
  const [exampleWords, setExampleWords] = useState<string[]>([]);
  const isMobile = useIsMobile();
  const { currentMode } = useGameMode();
  const navigate = useNavigate();
  
  const checkOverlayState = async () => {
    // Check if instructions have already been shown via welcome page
    const prefix = currentMode === 'practice' ? 'babblegram_practice_' : 'babblegram_';
    const instructionsShown = localStorage.getItem(`${prefix}instructions_shown`) === 'true';
    
    // If instructions were shown via welcome page, don't show this overlay
    if (instructionsShown) {
      console.log('Instructions already shown via welcome page, skipping overlay');
      setIsVisible(false);
      return;
    }
    
    const overlayState = await determineOverlayState(true); // Pass true to indicate this is an explicit check
    console.log('Instructions overlay state checked:', {
      overlayState,
      currentMode,
      instructionsShown,
      timestamp: new Date().toISOString()
    });
    
    // Set visibility based on overlay state, defaulting to false if properties don't exist
    setIsVisible(!!(overlayState.showInstructions || overlayState.showStreak));
    setShowStreak(!!(overlayState.showStreak));
  };
  
  useEffect(() => {
    setExampleWords(getExampleSequence());
    checkOverlayState();
    
    window.showInstructions = () => {
      // Offer option to go to welcome page instead
      const shouldGoToWelcome = window.confirm("Would you like to see the full welcome page with mode selection?");
      if (shouldGoToWelcome) {
        navigate('/');
        return;
      }
      
      setIsVisible(true);
      // Use mode-aware storage to get game status
      const gameStatus = modeAwareStorage.getGameStatus();
      setIsGameInProgress(gameStatus === 'playing');
    };
    return () => {
      delete window.showInstructions;
    };
  }, []);
  
  // Add mode switch listener to update overlay visibility
  useEffect(() => {
    const handleModeSwitch = () => {
      console.log('Mode switch detected in InstructionsOverlay, rechecking overlay state', {
        timestamp: new Date().toISOString()
      });
      
      // Reset example words for the new mode
      setExampleWords(getExampleSequence());
      
      // Re-check overlay state immediately
      checkOverlayState();
    };
    
    window.addEventListener('babblegram_mode_switched', handleModeSwitch);
    return () => {
      window.removeEventListener('babblegram_mode_switched', handleModeSwitch);
    };
  }, [currentMode]);

  const handleStartPlaying = () => {
    // Mark that instructions have been shown
    const prefix = currentMode === 'practice' ? 'babblegram_practice_' : 'babblegram_';
    localStorage.setItem(`${prefix}instructions_shown`, 'true');
    
    if (isGameInProgress) {
      console.log('Continuing existing game');
      setIsVisible(false);
      return;
    }
    console.log('Starting new game with level 1');
    saveGameState([], [], "playing", "", 1, [], 0, {}, null, false);
    setIsVisible(false);
  };

  if (!isVisible) return null;
  if (showStreak) {
    console.log('Rendering streak overlay');
    return <StreakOverlay />;
  }

  const level1NewLetterIndex = 0;
  const level2NewLetterIndex = exampleWords.length >= 2 ? findNewLetterIndex(exampleWords[0], exampleWords[1]) : 0;
  const level3NewLetterIndex = exampleWords.length >= 3 ? findNewLetterIndex(exampleWords[1], exampleWords[2]) : 0;
  const level4NewLetterIndex = exampleWords.length >= 4 ? findNewLetterIndex(exampleWords[2], exampleWords[3]) : 0;
  const level2NewLetter = exampleWords.length >= 2 ? getNewLetter(exampleWords[0], exampleWords[1]) : '';
  const level3NewLetter = exampleWords.length >= 3 ? getNewLetter(exampleWords[1], exampleWords[2]) : '';
  const level4NewLetter = exampleWords.length >= 4 ? getNewLetter(exampleWords[2], exampleWords[3]) : '';

  const ColorGuideSection = () => (
    <div className="grid grid-cols-3 gap-1.5">
      <div className="flex items-center gap-1">
        <div className="relative w-5 min-w-[20px]">
          <AspectRatio ratio={1/1}>
            <div className="absolute inset-0 bg-success rounded flex items-center justify-center text-white font-bold text-xs">A</div>
          </AspectRatio>
        </div>
        <span className="text-gray-300 text-[10px] whitespace-nowrap">Correct spot</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="relative w-5 min-w-[20px]">
          <AspectRatio ratio={1/1}>
            <div className="absolute inset-0 bg-warning rounded flex items-center justify-center text-white font-bold text-xs">B</div>
          </AspectRatio>
        </div>
        <span className="text-gray-300 text-[10px] whitespace-nowrap">Wrong spot</span>
      </div>
      <div className="flex items-center gap-1">
        <div className="relative w-5 min-w-[20px]">
          <AspectRatio ratio={1/1}>
            <div className="absolute inset-0 bg-destructive rounded flex items-center justify-center text-white font-bold text-xs">C</div>
          </AspectRatio>
        </div>
        <span className="text-gray-300 text-[10px] whitespace-nowrap">Not in word</span>
      </div>
    </div>
  );

  const LevelExampleRow = ({ 
    level, 
    word, 
    newLetterIndex, 
    newLetter, 
    color, 
    length 
  }: { 
    level: number; 
    word?: string; 
    newLetterIndex: number; 
    newLetter: string; 
    color: string; 
    length: number;
  }) => {
    const tileSize = "w-7 h-7";
    
    return (
      <div className="mb-1.5 flex items-center">
        <div className="min-w-max mr-3">
          <div className="flex gap-0.5">
            {Array.from({length}).map((_, i) => (
              <div 
                key={i} 
                className={`${tileSize} bg-success rounded flex items-center justify-center text-white font-bold text-lg mx-[3px]`}
                style={level > 1 && i === newLetterIndex ? {
                  border: '2px solid #de0046',
                  boxShadow: '0 0 3px #de0046'
                } : undefined}
              >
                {word?.[i]?.toUpperCase()}
              </div>
            ))}
          </div>
        </div>
        <div className="flex-1 h-7 flex flex-col justify-center">
          <h3 className={`${color} font-bold text-sm leading-none`}>Level {level}</h3>
          <p className="text-gray-300 text-xs leading-none">
            {length}-letter word {level > 1 && <span>• <span className="text-[#de0046] font-bold">{newLetter}</span> is new</span>}
          </p>
        </div>
      </div>
    );
  };

  return <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm" data-game-input-blocking-overlay="instructions">
      <div className="fixed left-1/2 top-1/2 w-full max-w-md -translate-x-1/2 -translate-y-1/2 space-y-1 rounded-xl bg-gray-900 p-3 shadow-xl" style={{
      border: "5px solid #454545",
      borderWidth: "5px"
    }}>
        <div className="flex items-center justify-center mb-0">
          <img src="/lovable-uploads/f8f44bc9-ce01-42f1-98ff-b6aad7a934e9.png" alt="BabbleGram Logo" className="h-8" />
        </div>
        
        <h2 className="text-xl font-bold text-center text-[#de0046] mb-0.5">How to Play</h2>
        
        <div className="space-y-1">
          <div className="grid grid-cols-2 gap-2 px-2 py-3">
            <div className="flex flex-col items-center justify-start bg-gray-800 rounded-lg p-2 h-full">
              <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center mb-2">
                <Book className="h-4 w-4 text-white" />
              </div>
              <p className="text-[#de0046] font-bold text-xs text-center mb-1">4 Puzzles</p>
              <p className="text-gray-300 text-xs text-center">Solve daily word chain</p>
            </div>
            
            <div className="flex flex-col items-center justify-start bg-gray-800 rounded-lg p-2 h-full">
              <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center mb-2">
                <Plus className="h-4 w-4 text-white" />
              </div>
              <p className="text-[#de0046] font-bold text-xs text-center mb-1">New Letter</p>
              <p className="text-gray-300 text-xs text-center">Extra letter each level</p>
            </div>
            
            <div className="flex flex-col items-center justify-start bg-gray-800 rounded-lg p-2 h-full">
              <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center mb-2">
                <ArrowRight className="h-4 w-4 text-white" />
              </div>
              <p className="text-[#de0046] font-bold text-xs text-center mb-1">Progress</p>
              <p className="text-gray-300 text-xs text-center">Fewer tries each level</p>
            </div>
            
            <div className="flex flex-col items-center justify-start bg-gray-800 rounded-lg p-2 h-full">
              <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center mb-2">
                <Target className="h-4 w-4 text-white" />
              </div>
              <p className="text-[#de0046] font-bold text-xs text-center mb-1">Streak</p>
              <p className="text-gray-300 text-xs text-center">Win daily to rank up</p>
            </div>
          </div>

          <div className="bg-gray-800 rounded-lg p-2.5">
            <LevelExampleRow 
              level={1} 
              word={exampleWords[0]} 
              newLetterIndex={level1NewLetterIndex} 
              newLetter="" 
              color="text-[#de0046]" 
              length={3} 
            />
            
            <LevelExampleRow 
              level={2} 
              word={exampleWords[1]} 
              newLetterIndex={level2NewLetterIndex} 
              newLetter={level2NewLetter} 
              color="text-[#8B5CF6]" 
              length={4} 
            />
            
            <LevelExampleRow 
              level={3} 
              word={exampleWords[2]} 
              newLetterIndex={level3NewLetterIndex} 
              newLetter={level3NewLetter} 
              color="text-[#0EA5E9]" 
              length={5} 
            />
            
            <LevelExampleRow 
              level={4} 
              word={exampleWords[3]} 
              newLetterIndex={level4NewLetterIndex} 
              newLetter={level4NewLetter} 
              color="text-[#22C55E]" 
              length={6} 
            />
          </div>

          <div className="bg-gray-800 rounded-lg p-2.5">
            <h3 className="text-gray-300 font-bold mb-1.5 text-xs">Color Guide</h3>
            <ColorGuideSection />
          </div>
        </div>

        <div className="flex justify-center mt-2">
          <Button onClick={() => { trackEvent(isGameInProgress ? 'continue_playing' : 'start_playing', { source: 'instructions_overlay', mode: currentMode }); handleStartPlaying(); }} className="bg-[#de0046] hover:bg-[#de0046]/90 py-4 px-6 text-sm mt-3 mb-3">
            {isGameInProgress ? 'Continue Playing' : 'Start Playing'}
          </Button>
        </div>

        <div className="text-gray-500 text-xs text-center w-full mt-1">
          © {new Date().getFullYear()} BabbleGram. Created by Dara Walsh
        </div>
      </div>
    </div>;
};

export default InstructionsOverlay;
